// eyeq — bulk-upload + ingestion-first ingest contracts (ADR-0037).
// Three-step flow: init (create catalog rows) → blob PUT (bytes → R2, dedup) →
// submit (attach MEQ metadata + on-device verdict, gate to in_work).
import { z } from 'zod';
import { EyeqDhashSchema } from './assets';
import { EyeqLocaleSchema } from './locales';
import { MeqAssetMetaSchema, MeqCategorySchema, MeqSubCategorySchema } from './meta';
import { EyeqBlobConflictReasonSchema } from '../vocab/eyeq';

// ── POST /api/eyeq/upload/init ──────────────────────────────────────────────
export const EyeqUploadInitReqSchema = z.object({
  participant_id: z.string().min(1),
  items: z
    .array(
      z.object({
        filename: z.string().min(1),
        // HEIC often uploads with an empty/'application/octet-stream' content-type;
        // the server detects the true format from the bytes, so this is advisory.
        content_type: z.string().default(''),
        size: z.number().int().nonnegative(),
        locale: EyeqLocaleSchema,
        category: MeqCategorySchema,
        sub_category: MeqSubCategorySchema,
      }),
    )
    .min(1)
    .max(200),
});
export type EyeqUploadInitReq = z.infer<typeof EyeqUploadInitReqSchema>;

export const EyeqUploadInitResSchema = z.object({
  assets: z.array(
    z.object({
      asset_id: z.string(),
      filename: z.string(),
      r2_key: z.string(),
      blob_url: z.string(), // PUT the raw bytes here (worker-proxied for v1)
    }),
  ),
});
export type EyeqUploadInitRes = z.infer<typeof EyeqUploadInitResSchema>;

// ── PUT /api/eyeq/upload/blob/:asset_id (raw body = file bytes) ──────────────
// Bytes are stored VERBATIM (original format preserved). format/ext/width/height
// are SERVER-detected from the bytes — the client content-type is not trusted.
// intake_notes surfaces the deterministic intake-gate verdicts (ADR-0039
// register: freshness / ≥12 MP / GPS presence) at the EARLIEST possible step so
// the client can flag a doomed file before the participant fills the form;
// submit re-checks the same gates authoritatively.
export const EyeqBlobResSchema = z.object({
  ok: z.boolean(),
  asset_id: z.string(),
  sha256: z.string(),
  duplicate: z.boolean(),
  format: z.string(), // detected mime (e.g. image/heic)
  ext: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  gps_present: z.boolean(),
  captured_at: z.string().nullable(), // EXIF DateTimeOriginal, verbatim
  intake_notes: z.array(z.string()),  // categorical: stale_capture | no_capture_date | below_min_pixels | gps_missing
});
export type EyeqBlobRes = z.infer<typeof EyeqBlobResSchema>;

/** 409 from either blob endpoint: a stored file cannot be replaced. Upload
 *  a different file with a new init/asset_id. Repeating identical bytes is
 *  idempotent and returns the existing EyeqBlobRes without changing QA. */
export const EyeqBlobConflictResSchema = z.object({
  ok: z.literal(false),
  error: EyeqBlobConflictReasonSchema,
});
export type EyeqBlobConflictRes = z.infer<typeof EyeqBlobConflictResSchema>;

// ── POST /api/eyeq/upload/archive?locale=&category=&sub_category= (raw .zip) ──
// The whole archive is one (locale, category, sub_category) cell; each image
// entry becomes its own asset with format/dims/EXIF indexed. Non-image entries
// (manifests, junk) are reported as skipped.
export const EyeqArchiveEntrySchema = z.object({
  filename: z.string(),
  asset_id: z.string().nullable(),
  ok: z.boolean(),
  format: z.string().optional(),
  width: z.number().int().nullable().optional(),
  height: z.number().int().nullable().optional(),
  sha256: z.string().optional(),
  duplicate: z.boolean().optional(),
  skipped: z.string().optional(),
  gps_present: z.boolean().optional(),
  captured_at: z.string().nullable().optional(),
  intake_notes: z.array(z.string()).optional(),
});
export type EyeqArchiveEntry = z.infer<typeof EyeqArchiveEntrySchema>;

export const EyeqArchiveResSchema = z.object({
  ok: z.boolean(),
  total: z.number().int(),
  indexed: z.number().int(),
  duplicates: z.number().int(),
  skipped: z.number().int(),
  entries: z.array(EyeqArchiveEntrySchema),
});
export type EyeqArchiveRes = z.infer<typeof EyeqArchiveResSchema>;

// ── POST /api/eyeq/upload/submit ────────────────────────────────────────────
export const EyeqUploadSubmitReqSchema = z.object({
  asset_id: z.string().min(1),
  // Optional/advisory: the short-edge gate uses SERVER-derived dims from the blob
  // step; these are only a fallback when the server couldn't parse the format.
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  ondevice_ok: z.boolean(),
  ondevice_reason: z.string().default(''),
  codes: z
    .array(z.object({ value: z.string().min(1), code_type: z.string().default('') }))
    .default([]),
  // 64-bit perceptual hash of the frame, computed where the pixels already are
  // (device / importer). The worker stores and compares it but never decodes an
  // image itself. Omitted = near-duplicate detection simply does not run.
  dhash: EyeqDhashSchema.optional(),
  meta: MeqAssetMetaSchema,
});
export type EyeqUploadSubmitReq = z.infer<typeof EyeqUploadSubmitReqSchema>;

export const EyeqQuotaCellSchema = z.object({
  category: z.string(),
  sub_category: z.string(),
  target: z.number().int(),
  collected: z.number().int(),
  remaining: z.number().int(),
  url_required: z.boolean(),
});
export type EyeqQuotaCell = z.infer<typeof EyeqQuotaCellSchema>;

export const EyeqUploadSubmitResSchema = z.object({
  ok: z.boolean(),
  asset_id: z.string(),
  status: z.string(), // in_work | rejected | duplicate
  quota: EyeqQuotaCellSchema.nullable(),
  notes: z.array(z.string()), // categorical, no PII (e.g. 'below_min_edge', 'over_quota')
  // The already-indexed frame this one looks like (same participant, same
  // cell, Hamming ≤ EYEQ_NEAR_DUP_BITS). Advisory: note 'near_dup_suspect' is
  // raised for the reviewer, the frame is NOT rejected on it.
  near_dup_of: z.string().nullable().default(null),
});
export type EyeqUploadSubmitRes = z.infer<typeof EyeqUploadSubmitResSchema>;

// ── GET /api/eyeq/quota?locale=XX ───────────────────────────────────────────
export const EyeqQuotaResSchema = z.object({
  locale: z.string(),
  cells: z.array(EyeqQuotaCellSchema),
  totals: z.object({ target: z.number().int(), collected: z.number().int() }),
});
export type EyeqQuotaRes = z.infer<typeof EyeqQuotaResSchema>;

// Minimum shortest-edge per Capture Protocol / ADR-0038 (on-device hard gate).
export const EYEQ_MIN_SHORT_EDGE = 2048;

// Intake gates wired 2026-07-30 (ADR-0039 enforcement gap register — all the
// deterministic non-URL rules). Thresholds come from the Capture Protocol; do
// not re-derive them from the PDFs.
export const EYEQ_MIN_PIXELS = 12_000_000;   // ≥ 12 MP (width × height)
export const EYEQ_FRESHNESS_DAYS = 62;       // "≤ 2 months" — 62 calendar days

// Отгрузка держит планку СТРОЖЕ приёма (решение владельца 2026-08-17): кадр
// стареет, пока лежит в очереди, и то, что было свежим на входе, к моменту
// сборки партии уже может не проходить. Приёмный гейт остаётся на 62 днях,
// иначе участник получал бы отказ за кадр, годный на момент загрузки.
export const EYEQ_DELIVERY_MAX_AGE_DAYS = 50;

// "No digital zoom (optical ok)" — EXIF DigitalZoomRatio (tag 0xA404). Phones
// write 1.0 (or ~1.002 rounding noise) for a plain shot, the crop factor for a
// sensor crop. The cap is 2.0 INCLUSIVE (owner decision 2026-07-31): Apple's
// "2x optical quality" 48 MP crop (~1.74–1.91) stays, anything past a doubling
// is a genuine digital crop. A missing tag is not a violation — most phones
// simply omit it.
export const EYEQ_MAX_DIGITAL_ZOOM = 2.0;

// DigitalZoomRatio alone cannot answer the question. Apple follows the EXIF
// spec and writes the digital factor; Samsung, Xiaomi and others write the
// TOTAL magnification, so a Galaxy S24 Ultra shot on its periscope reports
// "5" while using pure optics. Judging the tag on its own rejected 201 honest
// frames out of 2,909 in the 2026-08-10 pl_PL batch — all of them Android.
//
// What distinguishes the two is the focal length: a digital crop cannot change
// the lens. So the magnification a frame is ENTITLED to comes from its 35 mm
// equivalent, and only the excess over that is digital.
const EYEQ_MAIN_LENS_EQ_MM = 26;   // wide/main module across phone cameras
const EYEQ_MAX_OPTICAL_EQ_MM = 145; // beyond this no phone lens exists — it is a crop

/** How many times the frame exceeds what its own lens provides. 1.0 = none. */
export function eyeqZoomExcess(
  ratio: number | null | undefined, focal35mm: number | null | undefined,
): number {
  const z = ratio != null && Number.isFinite(ratio) && ratio > 0 ? ratio : null;
  const e = focal35mm != null && Number.isFinite(focal35mm) && focal35mm > 0 ? focal35mm : null;
  if (e == null) return z ?? 1;            // no focal length: the tag is all we have
  if (e > EYEQ_MAX_OPTICAL_EQ_MM) return e / EYEQ_MAIN_LENS_EQ_MM;
  const optical = Math.max(1, e / EYEQ_MAIN_LENS_EQ_MM);
  return Math.max(1, (z ?? 1) / optical);
}

/** null | 'zoom_digital'. A missing ratio AND missing focal length → null:
 *  most phones omit the tag entirely, and absence is not a violation. */
export function eyeqZoomNote(
  ratio: number | null | undefined, focal35mm?: number | null,
): string | null {
  if ((ratio == null || !Number.isFinite(ratio)) && focal35mm == null) return null;
  return eyeqZoomExcess(ratio, focal35mm) > EYEQ_MAX_DIGITAL_ZOOM ? 'zoom_digital' : null;
}

// ── URL gate (Capture Protocol Table 2 + URL Format Requirements) ───────────
// Deterministic FORMAT rules only — whether the page matches the photographed
// object is AI-eval/QA territory, never an intake gate. Shared verbatim by the
// worker submit gate and the cabinet's live field validation (hybrid model:
// the participant is asked for the URL but an EMPTY value never blocks —
// empties land in the staff backfill queue).
export const EYEQ_URL_PRODUCT_CAP = 10; // ≤10 captures per product per country

// "Never Use: Shortened URLs". maps.app.goo.gl is deliberately absent: the
// protocol itself gives it as the valid example for Business & Storefronts.
const URL_SHORTENERS = [
  'bit.ly', 'tinyurl.com', 'goo.gl', 't.co', 'ow.ly', 'is.gd',
  'buff.ly', 'rb.gy', 'tiny.cc',
];
// "Never Use: Social media links" (+ messengers). pinterest is matched by
// label because it ships country TLDs (pinterest.de, pinterest.co.uk).
const URL_SOCIAL = [
  'facebook.com', 'instagram.com', 'tiktok.com', 'twitter.com', 'x.com',
  'vk.com', 't.me', 'youtube.com', 'youtu.be',
];

function hostMatches(host: string, domains: string[]): boolean {
  return domains.some((d) => host === d || host.endsWith('.' + d));
}

// '' → null (empty is allowed; hybrid model), 'N/A' → null (protocol-sanctioned
// for natural elements / truly generic unbranded items). Otherwise:
// 'url_format' | 'url_shortener' | 'url_social' | null.
export function eyeqUrlFormatNote(url: string): string | null {
  const s = url.trim();
  if (s === '' || s === 'N/A') return null;
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return 'url_format';
  }
  if (u.protocol !== 'https:') return 'url_format'; // also kills app links (intent:// etc.)
  const host = u.hostname.toLowerCase().replace(/^www\./, '');
  if (host === 'maps.app.goo.gl') return null;
  if (hostMatches(host, URL_SHORTENERS)) return 'url_shortener';
  if (hostMatches(host, URL_SOCIAL) || /(^|\.)pinterest\./.test(host)) return 'url_social';
  return null;
}

// Product identity for the ≤10-per-country cap: lowercase host without www.,
// path without the trailing slash, query/fragment dropped. '' for empty/N/A/
// unparsable — those never participate in cap counting.
export function eyeqNormalizeUrl(url: string): string {
  const s = url.trim();
  if (s === '' || s === 'N/A') return '';
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return '';
  }
  const host = u.hostname.toLowerCase().replace(/^www\./, '');
  const path = u.pathname.replace(/\/+$/, '');
  return host + path;
}

// ── Каналы участников (2026-09-05, ADR-0087) ────────────────────────────────
// Кадр живого человека (бот приёмки, папки/архивы через participantFrame)
// всегда важнее скрапа карт в той же ячейке: в очереди QA он первый и не
// прячется за лимитом, в отгрузку идёт первым. Единственный список каналов —
// здесь; SQL-предикат строится из него, чтобы воркер и кабинет не разошлись.
export const EYEQ_PARTICIPANT_CHANNELS = ['participant', 'tgbot'] as const;
export const eyeqIsParticipantChannel = (via: string): boolean =>
  (EYEQ_PARTICIPANT_CHANNELS as readonly string[]).includes(via);
/** `0` — участник, `1` — всё остальное; для ORDER BY / ROW_NUMBER. */
export const eyeqChannelRankSql = (col: string): string =>
  `CASE WHEN ${col} IN (${EYEQ_PARTICIPANT_CHANNELS.map((v) => `'${v}'`).join(', ')}) THEN 0 ELSE 1 END`;
